import React, { useState } from 'react';
import Pagination from './Pagination';
import './BalanceOperationsTable.css';

const OPERATION_META = {
  'reserve_order:trader': { label: 'Reserve funds', tone: 'pending' },
  'cancel_order:trader': { label: 'Release reservation', tone: 'success' },
  'complete_pending_order:trader': { label: 'Write off reservation', tone: 'neutral' },
  'complete_deposit:gateway': { label: 'Credit balance', tone: 'success' },
  'complete_withdrawal:trader': { label: 'Credit balance', tone: 'success' },
  'complete_withdrawal:gateway': { label: 'Debit balance', tone: 'neutral' },
  'complete_cancelled_order:trader': { label: 'Debit balance', tone: 'neutral' },
  'complete_cancelled_order:gateway': { label: 'Credit balance', tone: 'success' },
  'credit_current:trader': { label: 'Credit balance', tone: 'success' },
  'manual_adjustment:gateway': { label: 'Manual debit', tone: 'warning' }
};

const formatDate = (value) => {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return { date: '-', time: '' };
  const date = new Date((timestamp < 1e12 ? timestamp * 1000 : timestamp));
  return {
    date: new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date),
    time: new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(date)
  };
};

const formatAmount = (value) => Number.isFinite(Number(value))
  ? Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : '—';

const BalanceSnapshot = ({ balance }) => {
  if (balance === null || balance === undefined) return <span className="balance-empty">—</span>;
  if (typeof balance !== 'object') return <span className="balance-single-value">{formatAmount(balance)}</span>;
  return <div className="balance-snapshot">
    <div><span>Current</span><strong>{formatAmount(balance.current)}</strong></div>
    <div><span>Pending</span><strong>{formatAmount(balance.pending)}</strong></div>
    <div className="balance-snapshot-total"><span>Total</span><strong>{formatAmount(balance.total)}</strong></div>
  </div>;
};

const BalanceChange = ({ before, after }) => {
  if (before === null || before === undefined || after === null || after === undefined) return <span className="balance-empty">—</span>;
  if (typeof before !== 'object' || typeof after !== 'object') {
    const change = Number(after) - Number(before);
    return <div className={`balance-change ${change < 0 ? 'is-negative' : 'is-positive'}`}><span>Balance</span><strong>{change > 0 ? '+' : ''}{formatAmount(change)}</strong></div>;
  }
  const fields = [['current', 'Current'], ['pending', 'Pending'], ['total', 'Total']]
    .map(([key, label]) => ({ label, change: Number(after[key]) - Number(before[key]) }))
    .filter(({ change }) => Number.isFinite(change) && Math.abs(change) > 0.000001);
  return <div className="balance-change-list">{fields.map(({ label, change }) => <div key={label} className={`balance-change ${change < 0 ? 'is-negative' : 'is-positive'}`}><span>{label}</span><strong>{change > 0 ? '+' : ''}{formatAmount(change)}</strong></div>)}</div>;
};

const BalanceOperationsTable = ({ operations = [], pagination, onPageChange }) => {
  const [copiedId, setCopiedId] = useState(null);

  const copyOrderId = async (orderId) => {
    try {
      await navigator.clipboard.writeText(orderId);
      setCopiedId(orderId);
      window.setTimeout(() => setCopiedId(null), 1600);
    } catch {
      setCopiedId(null);
    }
  };

  return <div className="balance-operations-container">
    <div className="balance-operations-table-wrap"><div className="balance-operations-table-scroll">
      <table className="balance-operations-table">
        <thead><tr>
          <th>Date</th><th>Operation</th><th>Account</th><th>Order ID</th>
          <th>Balance change</th><th>Balance after</th>
        </tr></thead>
        <tbody>{operations.map((operation) => {
          const entityName = operation.entityType === 'gateway' ? operation.gatewayName : operation.traderName;
          const entityId = operation.entityType === 'gateway' ? operation.gatewayId : operation.traderId;
          const date = formatDate(operation.createdAt);
          const meta = OPERATION_META[`${operation.action}:${operation.entityType}`]
            || { label: String(operation.action || 'Unknown').replace(/_/g, ' '), tone: 'neutral' };
          return <tr key={operation.ref || operation.operationId || `${operation.orderId}-${operation.createdAt}-${operation.action}`}>
            <td className="balance-mobile-date"><div className="orders-date-cell"><span>{date.date}</span><span>{date.time}</span></div></td>
            <td className="balance-mobile-operation"><span className={`balance-operation-action is-${meta.tone}`}>{meta.label}</span></td>
            <td className="balance-mobile-account"><div className="balance-operation-entity"><strong>{entityName || entityId || 'Unknown'}</strong></div></td>
            <td className="balance-mobile-order">{operation.orderId ? <div className="order-id-cell"><span className="order-id-text">{operation.orderId}</span><button type="button" className="copy-button" onClick={() => copyOrderId(operation.orderId)}>{copiedId === operation.orderId ? 'Done' : 'Copy'}</button></div> : '—'}</td>
            <td className="balance-mobile-change"><span className="balance-mobile-label">Balance change</span><BalanceChange before={operation.balanceBefore} after={operation.balanceAfter} /></td>
            <td className="balance-mobile-after"><span className="balance-mobile-label">Balance after</span><BalanceSnapshot balance={operation.balanceAfter} /></td>
          </tr>;
        })}{!operations.length && <tr><td colSpan="6" className="balance-operations-empty">No balance operations found</td></tr>}</tbody>
      </table>
    </div></div>
    {pagination && onPageChange && pagination.totalPages > 1 && <Pagination
      currentPage={pagination.page}
      totalPages={pagination.totalPages}
      onPageChange={onPageChange}
      itemsPerPage={pagination.limit}
      totalItems={pagination.total}
    />}
  </div>;
};

export default BalanceOperationsTable;
